import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Heart, LogOut, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, ownDancerIdParam } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { checkOutFromEvent } from "../lib/checkIn";
import { clearActiveCheckIn, setActiveCheckIn } from "../lib/activeCheckIn";
import { eventPublicPath } from "../lib/eventLinks";
import { goBack } from "../lib/navBack";
import { useResolvedEventId } from "../lib/useResolvedEventId";
import { Chip, StickerButton, StickerCard } from "../components/Sticker";
import { TabButton } from "../components/ui";
import CheckInFloorTab from "../components/checkIn/CheckInFloorTab";
import EventBoardTab from "../components/eventBoard/EventBoardTab";
import HostSchoolPromo from "../components/event/HostSchoolPromo";
import PullToRefresh from "../components/PullToRefresh";
import {
  BOARD_TAB_FLOOR_HINT,
  CHECK_IN_TAB_BOARD,
  CHECK_IN_TAB_FLOOR,
  CHECK_IN_POLL_MS,
} from "../lib/eventBoard/constants";
import { useEventBoardState } from "../lib/eventBoard/useEventBoardState";

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function PathResolveErrorCard({ message, onRetry, children }) {
  return (
    <div className="max-w-page mx-auto px-4 py-8">
      <StickerCard color="cream" className="p-6 text-center">
        <p className="font-ui font-semibold text-[20px] sm:text-[22px]">{message}</p>
        {children}
      </StickerCard>
    </div>
  );
}

export default function CheckInExperiencePage() {
  const {
    eventId: resolvedEventId,
    resolving,
    resolveError,
    retryResolve,
  } = useResolvedEventId();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const occurrenceDate = searchParams.get("occurrence_date");
  const initialTab = searchParams.get("tab") === "board" ? "board" : "floor";
  const [tab, setTab] = useState(initialTab);
  const { dancerId, bindDancerId } = useGuestIdentity();
  const { account, checking } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if ((!dancerId && !account) || !resolvedEventId) return;
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/events/${resolvedEventId}/check-in-page`, {
        params: {
          dancer_id: ownDancerIdParam(dancerId),
          occurrence_date: occurrenceDate || undefined,
        },
      });
      setData(r.data);
      const resolvedDancerId = r.data.my_rsvp?.dancer_id || dancerId;
      if (resolvedDancerId && resolvedDancerId !== dancerId) {
        bindDancerId(resolvedDancerId);
      }
      const checkedInAt = r.data.my_rsvp?.checked_in_at;
      if (checkedInAt) {
        setActiveCheckIn({
          event_id: resolvedEventId,
          dancer_id: resolvedDancerId,
          occurrence_date: r.data.my_rsvp?.occurrence_date || occurrenceDate,
          checked_in_at: checkedInAt,
          public_path: r.data.event?.public_path,
        });
      }
    } catch (e) {
      clearActiveCheckIn(resolvedEventId, occurrenceDate);
      if (!silent) {
        toast.error(formatApiError(e));
        setData(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dancerId, account, resolvedEventId, occurrenceDate, bindDancerId]);

  useEffect(() => {
    const fromUrl = searchParams.get("tab") === "board" ? "board" : "floor";
    setTab((current) => (current === fromUrl ? current : fromUrl));
  }, [searchParams]);

  function switchTab(next) {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "board") params.set("tab", "board");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  }

  function handleCheckInTabKeyDown(e) {
    const navKeys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!navKeys.includes(e.key)) return;

    const tabs = [...e.currentTarget.querySelectorAll('[role="tab"]')];
    if (tabs.length === 0) return;

    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1 && e.key !== "Home" && e.key !== "End") return;

    e.preventDefault();

    let nextIndex = currentIndex;
    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    }

    const nextTabKey = tabs[nextIndex]?.dataset?.tabKey;
    if (!nextTabKey) return;

    switchTab(nextTabKey);
    tabs[nextIndex]?.focus();
  }

  const event = data?.event;
  const mySubscription = data?.my_subscription || { is_favourite: false, on_profile: false };
  const isRecurring = event?.recurring && event.recurring !== "none";
  const checkedIn = data?.checked_in || [];
  const checkedInCount = checkedIn.length;
  const effectiveOccurrence = data?.my_rsvp?.occurrence_date || occurrenceDate;

  const {
    board,
    loading: boardLoading,
    error: boardError,
    reload: reloadBoard,
  } = useEventBoardState({
    eventId: resolvedEventId,
    dancerId,
    occurrenceDate: effectiveOccurrence,
  });

  const refreshAll = useCallback(async ({ silent = false } = {}) => {
    if (!dancerId || !resolvedEventId) return;
    if (!silent) setRefreshing(true);
    try {
      await Promise.all([
        load({ silent: true }),
        reloadBoard({ silent: true }),
      ]);
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [dancerId, resolvedEventId, load, reloadBoard]);

  useEffect(() => {
    if (!resolvedEventId || !dancerId) return undefined;

    load();
    let pollId = null;

    function startPolling() {
      if (pollId != null) return;
      pollId = window.setInterval(() => {
        if (document.visibilityState !== "visible") return;
        load({ silent: true });
        reloadBoard({ silent: true });
      }, CHECK_IN_POLL_MS);
    }

    function stopPolling() {
      if (pollId == null) return;
      window.clearInterval(pollId);
      pollId = null;
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        load({ silent: true });
        reloadBoard({ silent: true });
        startPolling();
      } else {
        stopPolling();
      }
    }

    if (document.visibilityState === "visible") {
      startPolling();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load, reloadBoard, resolvedEventId, dancerId]);

  const eventBackPath = useMemo(() => {
    if (!event) return null;
    const base = eventPublicPath(event);
    if (!base) return "/events";
    const occ = effectiveOccurrence;
    return `${base}${occ ? `?occurrence_date=${encodeURIComponent(occ)}` : ""}`;
  }, [event, effectiveOccurrence]);

  const signInNext = `${location.pathname}${location.search}`;
  const showSignInPrompt = !checking && !account;

  async function toggleFavouriteSeries() {
    if (!account) {
      toast.error("Sign in to follow this series");
      return;
    }
    if (!resolvedEventId || !isRecurring || mySubscription.is_favourite) return;
    try {
      const body = {
        is_favourite: true,
        on_profile: mySubscription.on_profile,
      };
      // Optional stale-guest merge hint only (identity comes from JWT).
      if (dancerId) body.dancer_id = dancerId;
      const r = await api.patch(`/events/${resolvedEventId}/subscription`, body);
      if (r.data.dancer_id && r.data.dancer_id !== dancerId) {
        bindDancerId(r.data.dancer_id);
      }
      setData((prev) => (prev ? { ...prev, my_subscription: r.data.subscription } : prev));
      toast.success("Following — you'll get updates in Notifications.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function checkout() {
    if ((!dancerId && !account) || !data?.my_rsvp || !resolvedEventId) return;
    setCheckingOut(true);
    try {
      await checkOutFromEvent(resolvedEventId, {
        dancerId,
        occurrenceDate: effectiveOccurrence,
      });
      // Clear any active check-in for this event (don't require occurrence match).
      clearActiveCheckIn(resolvedEventId);
      toast.success("Checked out. See you next time!");
      goBack(nav, location, [eventBackPath]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCheckingOut(false);
    }
  }

  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="font-display font-bold text-[20px]">Loading the floor...</p>
      </div>
    );
  }

  if (resolveError === "not_found") {
    return (
      <PathResolveErrorCard message="Event not found.">
        <Link to="/events" className="inline-block mt-4">
          <StickerButton>Back to events</StickerButton>
        </Link>
      </PathResolveErrorCard>
    );
  }

  if (resolveError === "error") {
    return (
      <PathResolveErrorCard message="Could not load this event. The server may be starting up.">
        <div className="mt-4 flex flex-col items-center gap-3">
          <StickerButton type="button" onClick={retryResolve}>
            Try again
          </StickerButton>
          <Link to="/events" className="font-display font-bold text-[14px] underline">
            Back to events
          </Link>
        </div>
      </PathResolveErrorCard>
    );
  }

  if (!dancerId) {
    return (
      <PathResolveErrorCard message="Set up your dancer profile first.">
        <Link to="/me" className="inline-block mt-4">
          <StickerButton>Go to profile</StickerButton>
        </Link>
      </PathResolveErrorCard>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="font-display font-bold text-[20px]">Loading the floor...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-page mx-auto px-4 py-8">
        <StickerCard color="cream" className="p-6 text-center">
          <p className="font-display font-bold text-[22px]">This floor page is only for checked-in dancers.</p>
          <Link to="/events" className="inline-block mt-4">
            <StickerButton>Back to events</StickerButton>
          </Link>
        </StickerCard>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-canvas text-ink font-body ${tab === "board" ? "pb-8" : "pb-20"}`}>
      <header className="max-w-page mx-auto px-4 pt-[max(1.25rem,env(safe-area-inset-top))] flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => nav("/events")}
          className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-2 min-h-[44px] font-ui font-medium text-[13px] hover:bg-canvas transition-colors"
          data-testid="check-in-back-to-events"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Events
        </button>
        <button
          type="button"
          onClick={checkout}
          disabled={checkingOut}
          className="inline-flex items-center gap-1.5 rounded-btn bg-night text-cream px-3 py-2 min-h-[44px] font-ui font-medium text-[13px] disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-[filter]"
          data-testid="check-in-checkout-btn"
        >
          <LogOut className="w-4 h-4" strokeWidth={3} />
          {checkingOut ? "Checking out..." : "Check out"}
        </button>
      </header>

      <PullToRefresh
        onRefresh={() => refreshAll()}
        refreshing={refreshing}
        testId="check-in-pull-refresh"
      >
      <main className={`max-w-page mx-auto px-4 mt-5 ${tab === "board" ? "space-y-4" : "space-y-5"}`}>
        <StickerCard className="p-5">
          <p className="font-ui font-medium text-[12px] tracking-wide text-ink/50">
            You&apos;re checked in
          </p>
          <h1 className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight mt-1 text-balance">
            {event.name}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip color="white">{formatWhen(event.starts_at)}</Chip>
            <Chip color="white">
              <MapPin className="w-3 h-3" strokeWidth={3} /> {event.location}{event.city ? `, ${event.city}` : ""}
            </Chip>
            <Chip color="amber">
              <Users className="w-3 h-3" strokeWidth={3} /> {checkedInCount} checked in
            </Chip>
          </div>
          {account && isRecurring && !mySubscription.is_favourite && (
            <button
              type="button"
              onClick={toggleFavouriteSeries}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-btn bg-white border border-black/[0.08] px-4 py-2.5 min-h-[44px] font-ui font-medium text-[14px] w-full hover:bg-canvas transition-colors"
              data-testid="check-in-favourite-series"
            >
              <Heart className="w-4 h-4" strokeWidth={2.5} aria-hidden />
              Follow this series
            </button>
          )}
        </StickerCard>

        <HostSchoolPromo
          hostSchool={event.host_school}
          hostSchoolOptOut={Boolean(event.host_school_opt_out)}
          isHosting={Boolean(data?.is_hosting)}
          event={event}
          eventName={event.name}
        />

        <div>
          <div
            className="grid grid-cols-2 gap-2 w-full"
            role="tablist"
            aria-label="Check-in sections"
            data-testid="check-in-tabs"
            onKeyDown={handleCheckInTabKeyDown}
          >
            <TabButton
              variant="checkin"
              active={tab === "floor"}
              onClick={() => switchTab("floor")}
              testid="check-in-tab-floor"
              panelId="check-in-panel-floor"
              tabKey="floor"
            >
              {CHECK_IN_TAB_FLOOR}
            </TabButton>
            <TabButton
              variant="checkin"
              active={tab === "board"}
              onClick={() => switchTab("board")}
              testid="check-in-tab-board"
              panelId="check-in-panel-board"
              tabKey="board"
            >
              {CHECK_IN_TAB_BOARD}
            </TabButton>
          </div>
          {tab === "floor" && (
            <p className="mt-2 text-[13px] font-medium text-ink/70 leading-snug max-w-[65ch]">
              {BOARD_TAB_FLOOR_HINT}
            </p>
          )}
        </div>

        {tab === "floor" ? (
          <div
            id="check-in-panel-floor"
            role="tabpanel"
            aria-labelledby="check-in-tab-floor"
            tabIndex={0}
          >
            <CheckInFloorTab
              showSignInPrompt={showSignInPrompt}
              signInNext={signInNext}
              checkedIn={checkedIn}
            />
          </div>
        ) : (
          <div
            id="check-in-panel-board"
            role="tabpanel"
            aria-labelledby="check-in-tab-board"
            tabIndex={0}
          >
            <EventBoardTab
              eventId={resolvedEventId}
              dancerId={dancerId}
              occurrenceDate={effectiveOccurrence}
              board={board}
              loading={boardLoading}
              error={boardError}
              onRetry={reloadBoard}
            />
          </div>
        )}
      </main>
      </PullToRefresh>
    </div>
  );
}
